export const CONSIGNMENT_RULE_TYPES = {
  LINE_DIRECT: 'LINE_DIRECT',
  FIXED_QUANTITY: 'FIXED_QUANTITY',
  WEIGHT_FORMULA: 'WEIGHT_FORMULA',
};

export const CONSIGNMENT_RULE_TYPE_LABEL = {
  LINE_DIRECT: 'Quantité directe',
  FIXED_QUANTITY: 'Nombre par palette',
  WEIGHT_FORMULA: 'Formule au poids',
};

/** Types autorisés en création (FIXED_QUANTITY débloqué par FLEETC-3068). */
export const CONSIGNMENT_RULE_TYPE_CREATE_OPTIONS = [
  CONSIGNMENT_RULE_TYPES.LINE_DIRECT,
  CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA,
  CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY,
].map((value) => ({
  value,
  name: CONSIGNMENT_RULE_TYPE_LABEL[value],
}));

export const CONSIGNMENT_WEIGHT_UNIT_LABEL = {
  t: 'Tonnes',
  kg: 'Kilogrammes',
};

/**
 * Unités proposées dans le formulaire : KGM n'étant pas implémenté
 * (l'évaluateur ignore l'unité et calcule en tonnes), on ne propose que TONNES.
 */
export const CONSIGNMENT_WEIGHT_UNIT_OPTIONS = [
  { value: 't', name: CONSIGNMENT_WEIGHT_UNIT_LABEL.t },
];

/** Unité de poids par défaut du formulaire (seule TONNES est supportée). */
export const CONSIGNMENT_DEFAULT_WEIGHT_UNIT = 't';

/** Aide contextuelle affichée sous chaque type de règle. */
export const CONSIGNMENT_RULE_TYPE_HINT = {
  [CONSIGNMENT_RULE_TYPES.LINE_DIRECT]:
    'La quantité commandée correspond directement au nombre de palettes (ex. une ligne « palette », quantité 3 → 3 palettes).',
  [CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY]:
    'Un nombre fixe de produits remplit une palette (ex. 40 sacs = 1 palette). Arrondi au supérieur.',
  [CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA]:
    "Une palette correspond à un poids donné (ex. 1 tonne). Palettes = poids total ÷ poids d'une palette, arrondi au supérieur.",
};

export function getConsignmentRuleTypeOptions() {
  return CONSIGNMENT_RULE_TYPE_CREATE_OPTIONS;
}

/**
 * Convertit le multiplicateur stocké (fixed_quantity = 1/N) en nombre de
 * produits par palette (N) affiché à l'utilisateur.
 */
export function fixedQuantityToProductsPerPallet(fixedQuantity) {
  if (fixedQuantity == null || fixedQuantity === '') return '';
  const num = Number(fixedQuantity);
  if (Number.isNaN(num) || num <= 0) return '';
  return String(Math.round(1 / num));
}

/**
 * Convertit le nombre de produits par palette (N) saisi par l'utilisateur en
 * multiplicateur attendu par l'API (fixed_quantity = 1/N).
 */
export function productsPerPalletToFixedQuantity(productsPerPallet) {
  const n = Number(productsPerPallet);
  if (Number.isNaN(n) || n <= 0) return null;
  return 1 / n;
}

export function formatRuleNumber(value) {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return String(parseFloat(num.toFixed(4)));
}

export const CONSIGNMENT_WEIGHT_UNIT_SHORT = {
  t: 't',
  kg: 'kg',
};

export function formatWeightUnitShort(weightUnit) {
  if (!weightUnit) return 'kg';
  const normalized = String(weightUnit).toLowerCase();
  return CONSIGNMENT_WEIGHT_UNIT_SHORT[normalized] || normalized;
}

export function formatConsignmentRuleParams(rule) {
  if (!rule?.rule_type) return '-';

  switch (rule.rule_type) {
    case CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY: {
      if (rule.fixed_quantity == null) return '-';
      const productsPerPallet = fixedQuantityToProductsPerPallet(
        rule.fixed_quantity
      );
      return productsPerPallet ? `${productsPerPallet} / palette` : '-';
    }
    case CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA: {
      if (rule.weight_divisor == null) return '-';
      const divisor = formatRuleNumber(rule.weight_divisor);
      const unit = formatWeightUnitShort(rule.weight_unit);
      return `${divisor}${unit} / palette`;
    }
    default:
      return '1 palette / ligne';
  }
}

export function normalizeConsignmentRule(item) {
  return {
    ...item,
    id: item.rule_id ?? item.id,
    is_active: item.is_active === 1 || item.is_active === true,
  };
}

/**
 * Adapte la réponse paginée API → format attendu par AgrivitechPagination.
 * Gère le format direct { total, page, limit, data } ou enveloppé { data: { ... } }.
 */
export function adaptConsignmentRulesListResponse(responseData) {
  const payload =
    responseData?.data?.data != null ? responseData.data : responseData;
  const { total = 0, page = 1, limit = 20, data = [] } = payload ?? {};

  return {
    pagination_info: {
      current_items_count: data.length,
      items_per_page: limit,
      total_items: total,
      current_page: page,
      total_pages: Math.max(1, Math.ceil(total / limit)),
    },
    items: data.map(normalizeConsignmentRule),
  };
}

export function getConsignmentRuleApiErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Une erreur est survenue.';

  // Le message API est déjà lisible : on l'affiche directement.
  return data.message || data.error || 'Une erreur est survenue.';
}
